import { Link } from "@tanstack/react-router";
import { addSeconds, formatDistance, isToday, isTomorrow } from "date-fns";
import { useMemo } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import { formatCurrency } from "@/helpers/currency";
import { datetimeToJS } from "@/helpers/parsers";
import { cn } from "@/lib/utils";

import type { BookingType } from "./graphql";

interface BookingProps {
  booking: BookingType;
}

export default function Booking({ booking }: BookingProps) {
  const { details, diffLabel } = useMemo(() => {
    const details: string[] = [];
    const dt = addSeconds(
      datetimeToJS(booking.datetime),
      booking.datetimeOffset
    );

    const diffLabel = (() => {
      if (isToday(dt)) {
        return "Today";
      }
      if (isTomorrow(dt)) {
        details.push("Tomorrow");
        return "Tomorrow";
      }

      const diff = formatDistance(new Date(), dt);

      return `in ${diff}`;
    })();

    details.push(
      new Intl.DateTimeFormat("default", { dateStyle: "medium" }).format(dt)
    );
    details.push(
      new Intl.DateTimeFormat("default", {
        dateStyle: undefined,
        timeStyle: "short",
      }).format(dt)
    );
    details.push(formatCurrency(booking.total, booking.currency));

    return { details: details.join(" | "), diffLabel };
  }, [booking]);

  return (
    <div className="group relative w-[91%] shrink-0 snap-center md:w-full">
      <div
        className={cn(
          "relative aspect-[343/168] overflow-hidden rounded-[10px] shadow-white/20 transition-shadow group-hover:shadow-0-0-10 md:aspect-[568/168]",
          'after:absolute after:inset-0 after:size-full after:bg-linear-[180deg,#00000080_10%,#00000000_25%,#00000000_37%,#00000080_64%] after:content-[""]'
        )}
      >
        <CloudinaryImage className="size-full" game={booking.game} />
      </div>
      <p className="absolute top-3 left-3.5 flex h-6.5 items-center rounded-[3px] bg-black/60 px-1.5 text-12 text-white">
        {diffLabel}
      </p>
      <div className="absolute inset-0 flex size-full flex-col items-start justify-end gap-0.5 px-4 py-3">
        <h4 className="font-medium text-16/[1.5em] text-white">
          <Link className="link-overlay" to={`/booking/${booking.id}`}>
            {booking.game.name}
          </Link>
        </h4>
        <h5 className="font-normal text-11/[1.2em] text-white/80">
          {booking.game.location.displayName ||
            booking.game.location.company.name}
        </h5>
        <p className="line-clamp-1 text-11/[1.2em] text-white/80">{details}</p>
      </div>
    </div>
  );
}
